import { emit, on as sdkOn } from "./eventEmitter";

interface CdialWidgetOptions {
  agentId: string;
  mountUI?: boolean;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class CdialWidget {
  private agentId: string;
  private mountUI: boolean = false;
  public widgetTheme: any = null;
  private LOCAL_STORAGE_KEY = "hasInteractedWithWidget";
  private hasInteracted: boolean;
  private call_ongoing: boolean = false;

  private callOnIcon: string;
  private callOffIcon: string;
  private callOutgoingIcon: string;
  private callBox!: HTMLDivElement;
  private container!: HTMLDivElement;
  private avatar!: HTMLDivElement;
  private content!: HTMLDivElement;
  private text!: HTMLParagraphElement;
  private button!: HTMLButtonElement;
  private poweredBy!: HTMLParagraphElement;
  private termsModal!: HTMLDivElement;
  private termsTitle!: HTMLHeadingElement;
  private termsText!: HTMLParagraphElement;
  private buttonContainer!: HTMLDivElement;
  private cancelButton!: HTMLButtonElement;
  private agreeButton!: HTMLButtonElement;
  private callBoxAnimation!: NodeJS.Timeout;
  private buttonAnimation!: NodeJS.Timeout;
  public on = sdkOn;

  constructor(data: CdialWidgetOptions) {
    this.widgetTheme = null;
    this.LOCAL_STORAGE_KEY = "hasInteractedWithWidget";
    this.hasInteracted =
      localStorage.getItem(this.LOCAL_STORAGE_KEY) === "true";
    this.call_ongoing = false;
    this.agentId = data.agentId;

    this.callOnIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-phone"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;

    this.callOffIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-phone-off"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="22" x2="2" y1="2" y2="22"/></svg>`;

    this.callOutgoingIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-phone-outgoing"><polyline points="22 8 22 2 16 2"/><line x1="16" x2="22" y1="8" y2="2"/><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;
  
    this.mountUI = data.mountUI !== undefined ? data.mountUI : false;
  }

  async init() {
    this.agentId = this.agentId;
    if (!this.agentId)
      return console.error("Agent ID not found in indigenius-convai element");
    this.loadNunitoFont();
    const theme = await this.fetchWidgetTheme();
    this.widgetTheme = theme;
    if(this.mountUI){
      this.createElements();
      this.mountElements();
      this.setupTermsModal();
      this.animateCallBox();
      this.setupCallButton();
    }
  }

  public destroy() {
    if (this.callBoxAnimation) clearInterval(this.callBoxAnimation);
    if (this.buttonAnimation) clearTimeout(this.buttonAnimation);

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    this.callBox = null!;
    this.container = null!;
    this.avatar = null!;
    this.content = null!;
    this.text = null!;
    this.button = null!;
    this.poweredBy = null!;
    this.termsModal = null!;
    this.termsTitle = null!;
    this.termsText = null!;
    this.buttonContainer = null!;
    this.cancelButton = null!;
    this.agreeButton = null!;
    this.callBoxAnimation = null!;
    this.buttonAnimation = null!;
    emit("widget-destroyed", {});
    this.call_ongoing = false;
  }

  createElements() {
    const theme = this.widgetTheme;

    this.container = document.createElement("div");
    this.container.id = "cdial-widget-container";
    Object.assign(this.container.style, {
      position: "fixed",
      bottom: "10px",
      right: "12px",
      fontFamily: `"Nunito", sans-serif`,
      zIndex: 1500,
      backgroundColor: "white",
    });

    this.callBox = document.createElement("div");
    this.callBox.id = "cdial-widget-call-box";
    Object.assign(this.callBox.style, {
      width: "265px",
      backgroundColor: theme?.background_color || "#ffffff",
      color: theme?.text_color,
      borderRadius: theme?.card_radius + "px",
      border: `1px solid ${theme?.border_color}`,
      padding: "5px 20px 16px 20px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      bottom: "-100px",
      transition: "transform 1s ease-out",
    });

    this.avatar = document.createElement("div");
    this.avatar.id = "cdial-widget-avatar";
    Object.assign(this.avatar.style, {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      backgroundColor: "#D9D9D9",
      flexShrink: "0",
      overflow: "hidden",
    });

    if (theme?.avatar) {
      const avatarImg = document.createElement("img");
      avatarImg.id = "cdial-widget-avatar-img";
      avatarImg.src = theme.avatar;
      Object.assign(avatarImg.style, {
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        objectFit: "cover",
      });
      avatarImg.onerror = (error) => {
        console.error("An error occurred loading avatar ", error);
        avatarImg.remove();
        this.loadIridescence(this.avatar);
      };
      this.avatar.appendChild(avatarImg);
    } else {
      this.loadIridescence(this.avatar);
    }

    this.content = document.createElement("div");
    this.content.id = "cdial-widget-content";
    Object.assign(this.content.style, {
      display: "flex",
      flexDirection: "column",
      alignItems: "start",
      width: "100%",
    });

    this.text = document.createElement("p");
    this.text.id = "cdial-widget-text";
    this.text.innerText = theme?.call_to_action || "Call now";
    Object.assign(this.text.style, {
      fontFamily: `"Nunito", sans-serif`,
      fontSize: "16px",
      textAlign: "left",
    });

    this.button = document.createElement("button");
    this.button.id = "cdial-widget-button";
    this.button.innerText = theme?.start_call_button || "Start Call";
    Object.assign(this.button.style, {
      backgroundColor: theme?.button_color,
      color: theme?.button_text_color,
      border: "none",
      padding: "10px 16px",
      borderRadius: theme?.button_radius + "px",
      cursor: "pointer",
      fontSize: "14px",
      width: "95%",
      transition: "transform 0.6s ease-in-out",
      fontFamily: `"Nunito", sans-serif`,
      outlineColor: theme?.focus_outline_color || "transparent",
      outlineWidth: "2px",
      outlineStyle: "solid",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    });

    this.poweredBy = document.createElement("p");
    this.poweredBy.innerText = "Powered by indigenius AI Agent Pro";

    Object.assign(this.poweredBy.style, {
      fontSize: "12px",
      color: "#888",
      textAlign: "center",
      marginTop: "8px",
      fontFamily: `"Nunito", sans-serif`,
    });

    // Animate CallBox every 4 seconds
    this.callBoxAnimation = setInterval(() => {
      this.callBox.style.transform = "scale(1.05)";
      setTimeout(() => {
        this.callBox.style.transform = "scale(1)";
      }, 600);
    }, 4000);

    // Animate Button every 6 seconds
    this.buttonAnimation = setInterval(() => {
      this.button.style.transform = "scale(1.04)";
      setTimeout(() => {
        this.button.style.transform = "scale(1)";
      }, 600);
    }, 6000);

    if (this.hasInteracted) {
      // clearInterval(callBoxAnimation);
      // clearInterval(buttonAnimation);
    }
  }

  mountElements() {
    this.callBox.appendChild(this.avatar);
    this.content.appendChild(this.text);
    this.content.appendChild(this.button);
    this.callBox.appendChild(this.content);
    this.container.appendChild(this.callBox);

    this.content.appendChild(this.button);
    this.container.appendChild(this.poweredBy);

    document.body.appendChild(this.container);
  }

  async loadIridescence(container: HTMLDivElement) {
    const iridescenceContainer = document.createElement("div");
    iridescenceContainer.id = "iridescence-container";
    container.appendChild(iridescenceContainer);
    const module = await import("./iridescence");
    module.createIridescence(iridescenceContainer);
  }

  animateCallBox() {
    setTimeout(() => {
      this.callBox.style.transform = "translateY(-13px)";
      setTimeout(() => {
        this.callBox.style.transform = "translateY(0px)";
      }, 300);
    }, 100);
  }

  setupTermsModal() {
    this.termsModal = document.createElement("div");
    this.termsModal.id = "terms-modal";
    Object.assign(this.termsModal.style, {
      display: "none",
      marginBottom: "25px",
      marginRight: "5px",
      bottom: "-100px",
      marginLeft: "15px",
      backgroundColor: "#fff",
      padding: "20px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      borderRadius: "16px",
      border: `1px solid #EEEEEE`,
      maxWidth: "400px",
      textAlign: "left",
      transition: "transform 0.6s ease-out",
    });

    const updateModalWidth = () => {
      this.termsModal.style.maxWidth =
        window.innerWidth < 768 ? "500px" : "500px";
    };

    updateModalWidth();
    window.addEventListener("resize", updateModalWidth);

    this.termsTitle = document.createElement("h2");
    this.termsTitle.textContent = "Terms and Conditions";
    Object.assign(this.termsTitle.style, {
      fontSize: "18px",
      marginBottom: "-10px",
    });

    this.termsText = document.createElement("p");
    this.termsText.textContent = this.widgetTheme?.terms_and_condition_content;
    Object.assign(this.termsText.style, {
      fontSize: "14px",
      marginBottom: "16px",
    });

    this.buttonContainer = document.createElement("div");
    Object.assign(this.buttonContainer.style, {
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
    });

    this.cancelButton = document.createElement("button");
    this.cancelButton.textContent = "Cancel";
    this.cancelButton.id = "cancel-terms";

    Object.assign(this.cancelButton.style, {
      padding: "8px 12px",
      border: "none",
      backgroundColor: "transparent",
      borderRadius: "4px",
      minWidth: "80px",
      height: "40px",
      fontFamily: `"Nunito", sans-serif`,
      cursor: "pointer",
    });

    this.cancelButton.onclick = () => {
      this.termsModal.style.transform = "translateY(250px)";
      setTimeout(() => {
        this.callBox.style.display = "flex";
        this.poweredBy.style.display = "block";
        this.termsModal.style.display = "none";
      }, 600);
    };

    this.agreeButton = document.createElement("button");
    this.agreeButton.textContent = "Agree";
    this.agreeButton.id = "agree-terms";

    Object.assign(this.agreeButton.style, {
      padding: "8px 12px",
      border: "none",
      backgroundColor: this.widgetTheme?.button_color || "#f57c00",
      color: "#000000",
      fontFamily: `"Nunito", sans-serif`,
      borderRadius: "8px",
      cursor: "pointer",
      minWidth: "120px",
      height: "40px",
    });

    this.agreeButton.onclick = () => {
      this.termsModal.style.transform = "translateY(250px)";
      setTimeout(() => {
        this.termsModal.style.display = "none";
        this.callBox.style.display = "flex";
        this.poweredBy.style.display = "block";
        window.myCdialWidget.startCall(this.widgetTheme);
        this.callBox.style.transform = "translateY(-10px)";
      }, 1000);
    };

    this.container.appendChild(this.termsModal);
    this.termsModal.appendChild(this.termsTitle);
    this.termsModal.appendChild(this.termsText);
    this.termsModal.appendChild(this.buttonContainer);
    this.buttonContainer.appendChild(this.cancelButton);
    this.buttonContainer.appendChild(this.agreeButton);
  }

  setupCallButton() {
    if (this.button) {
      this.button?.addEventListener("click", () => {
      this.toggleCall();
    });

    localStorage.setItem(this.LOCAL_STORAGE_KEY, "true");
    emit("start-call", {
      agentId: this.agentId,
      widgetTheme: this.widgetTheme,
    });
  }
  }

  toggleCall() {
    if (!this.hasInteracted) {
      this.hasInteracted = true;
      localStorage.setItem(this.LOCAL_STORAGE_KEY, "true");
      clearInterval(this.callBoxAnimation);
      clearInterval(this.buttonAnimation);
    }

    if (this.widgetTheme?.terms_and_condition && !this.call_ongoing && this.mountUI) {
      this.termsModal.style.display = "block";
      this.callBox.style.display = "none";
      this.poweredBy.style.display = "none";
      this.callBox.style.transform = "translateY(100px)";
      this.termsModal.style.display = "block";
      setTimeout(() => {
        this.termsModal.style.transform = "translateY(-10px)";
      }, 100);
    } else {
      if (!this.call_ongoing) {
        this.call_ongoing = true;
        this.setupWidgetListener();
        console.log(this.widgetTheme)
        window.myCdialWidget.startCall(this.widgetTheme);
      } else {
        this.call_ongoing = false;
        window.myCdialWidget.endCall();
      }
    }
  }

  setupWidgetListener() {
    if (
      !window.myCdialWidget ||
      typeof window.myCdialWidget.on !== "function"
    ) {
      console.error(
        "myCdialWidget is not initialized or invalid:",
        window.myCdialWidget
      );
      return;
    }
    
    if(!this.mountUI) return;

    window.myCdialWidget.on("dialing", () => {
      this.button.innerHTML = `${this.callOutgoingIcon} ${
        this.widgetTheme?.dialCallButtonText || "Dialing..."
      }`;
      this.button.style.backgroundColor = `#F79320`;
    });

    window.myCdialWidget.on("connected", async () => {
      await sleep(1000);
      this.call_ongoing = true;
      this.button.innerHTML = `${this.callOffIcon} ${this.widgetTheme?.end_call_button}`;
      this.button.style.backgroundColor = `#EB202A`;
    });

    window.myCdialWidget.on("ended", async () => {
      this.call_ongoing = false;
      this.button.innerHTML = `${this.callOnIcon} ${this.widgetTheme?.start_call_button}`;
      this.button.style.backgroundColor = this.widgetTheme?.button_color;
    });

    window.myCdialWidget.on("transcript", (text: string) => {
      console.log("Transcript Received:", text);
    });

    window.myCdialWidget.on("error", async (err: Error) => {
      await sleep(3000);
      this.call_ongoing = false;
      this.button.innerHTML = `${this.callOnIcon} ${this.widgetTheme?.start_call_button}`;
      console.error("Error:", err);
    });
  }

  async fetchWidgetTheme() {
    try {
      const response = await fetch(
        `https://indigenius.shop/widget/details/${this.agentId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) throw new Error("Failed to fetch widget theme");

      const data = await response.json();
      emit("widgetTheme", data?.details);

      return data?.details;
    } catch (error) {
      emit("error", error);
    }
  }

  loadNunitoFont() {
    const nunitoFont = document.createElement("link");
    nunitoFont.rel = "stylesheet";
    nunitoFont.href =
      "https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;700&display=swap";
    document.head.appendChild(nunitoFont);
  }
}
